import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Crown, CheckCircle2, AlertCircle } from "lucide-react";
import axios from "axios";
import StarCanvas from "@/components/StarCanvas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const pollRef = useRef(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await axios.get(`${API}/payments/stripe/status/${sessionId}`, { withCredentials: true });
        if (data.payment_status === "paid") {
          clearInterval(pollRef.current);
          setState("paid");
          return;
        }
      } catch (_) {}
      if (attemptsRef.current >= 15) {
        clearInterval(pollRef.current);
        setState("timeout");
      }
    };
    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => clearInterval(pollRef.current);
  }, []);

  return (
    <div className="relative min-h-screen bg-[#0B091A] flex items-center justify-center px-4">
      <StarCanvas />
      <div className="relative z-10 glass-card rounded-3xl p-10 sm:p-12 max-w-md w-full text-center">
        {state === "checking" && (
          <>
            <Loader2 size={40} className="text-amber-400 animate-spin mx-auto mb-6" />
            <h2 data-testid="payment-success-checking" className="font-serif text-2xl font-bold text-white mb-2">Confirming your payment...</h2>
            <p className="text-sm text-slate-500">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={28} className="text-emerald-400" />
            </div>
            <h2 data-testid="payment-success-title" className="font-serif text-2xl font-bold text-white mb-2">Welcome to DreamWeaver Premium!</h2>
            <p className="text-sm text-slate-400 mb-8">Your subscription is active. Unlimited bedtime adventures await.</p>
            <button
              data-testid="payment-success-continue-btn"
              onClick={() => navigate("/my-stories")}
              className="px-8 py-3 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white font-semibold text-sm hover:shadow-lg hover:shadow-purple-500/30 transition-all inline-flex items-center gap-2"
            >
              <Crown size={14} /> Go to My Stories
            </button>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <div className="w-16 h-16 rounded-full bg-amber-500/15 border border-amber-400/30 flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={26} className="text-amber-400" />
            </div>
            <h2 data-testid="payment-success-pending-title" className="font-serif text-2xl font-bold text-white mb-2">Still confirming...</h2>
            <p className="text-sm text-slate-400 mb-8">
              Your payment may still be processing. Check My Stories in a moment — it'll unlock automatically once confirmed.
            </p>
            <Link
              to="/my-stories"
              className="px-8 py-3 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white font-semibold text-sm inline-flex items-center gap-2"
            >
              Go to My Stories
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
